// components/analytics/AnalyticsDashboard.jsx
// Updated: Shows REAL user data or honest empty state - NO fake stats
import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Award, ShoppingBag, Coins, Share2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTokens } from '../../context/TokenContext';
import { formatTokens, formatTokenValue, getDaysUntilExpiration } from '../../utils/tokenEconomy';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const { balance, transactions } = useTokens();
  
  const [userMetrics, setUserMetrics] = useState({
    tokenBalance: 0,
    tokensEarned: 0,
    tokensSpent: 0,
    ordersCompleted: 0,
    groupsJoined: 0,
    jobsCompleted: 0,
    referralsMade: 0,
    totalSaved: 0,
    trustScore: 3.0
  });
  
  const [expiringTokens, setExpiringTokens] = useState({
    amount: 0,
    daysUntil: null
  });
  
  // Calculate real user metrics from actual data
  useEffect(() => {
    if (user && isLoggedIn) {
      // Get real transaction data
      const userTransactions = transactions || [];
      
      // Calculate earnings (tokens received)
      const earned = userTransactions
        .filter(t => t.type === 'earn' || t.type === 'job_completion' || t.type === 'referral')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      // Calculate spending (tokens redeemed)
      const spent = userTransactions
        .filter(t => t.type === 'redeem' || t.type === 'spend')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      // Get stats from user object
      const stats = user.stats || {};
      
      setUserMetrics({
        tokenBalance: balance || 0,
        tokensEarned: earned || stats.tokensEarned || 0,
        tokensSpent: spent || stats.tokensSpent || 0,
        ordersCompleted: stats.ordersCompleted || stats.groupsCompleted || 0,
        groupsJoined: stats.groupsJoined || 0,
        jobsCompleted: stats.jobsCompleted || 0,
        referralsMade: stats.referralsMade || 0,
        totalSaved: stats.totalSaved || 0,
        trustScore: user.trust?.score || user.reliabilityScore?.overall || 3.0
      });
      
      // Check for expiring tokens
      const oldestTransaction = userTransactions
        .filter(t => t.type === 'earn')
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))[0];
      
      if (oldestTransaction) {
        const daysLeft = getDaysUntilExpiration(oldestTransaction.timestamp);
        if (daysLeft <= 30 && daysLeft > 0) {
          setExpiringTokens({
            amount: oldestTransaction.amount,
            daysUntil: daysLeft
          });
        }
      }
    }
  }, [user, isLoggedIn, transactions, balance]);
  
  // Not logged in state
  if (!isLoggedIn) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
          <TrendingUp className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="font-semibold text-gray-700 mb-2">Track Your Impact</h3>
        <p className="text-gray-500 text-sm mb-4">
          Sign in to see your savings, tokens earned, and community impact.
        </p>
        <button
          onClick={() => navigate('/login')}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Sign In
        </button>
      </div>
    );
  }
  
  // New user with no activity
  const hasActivity = userMetrics.ordersCompleted > 0 || 
                      userMetrics.tokensEarned > 0 || 
                      userMetrics.jobsCompleted > 0;
  
  if (!hasActivity) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Your UniLedge Journey</h3>
        
        {/* Welcome Token Balance */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Your Token Balance</p>
              <p className="text-2xl font-bold text-yellow-600">{formatTokens(userMetrics.tokenBalance)}</p>
              <p className="text-xs text-gray-500">{formatTokenValue(userMetrics.tokenBalance)} value</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Coins className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        {/* Get Started Actions */}
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-2">Start building your impact:</p>
          
          <button
            onClick={() => navigate('/deals?tab=trending')}
            className="w-full flex items-center justify-between bg-white p-3 rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <ShoppingBag className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-800">Make Your First Purchase</p>
                <p className="text-xs text-gray-500">Save 25-40% on household items</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
          
          <button
            onClick={() => navigate('/earn?tab=share')}
            className="w-full flex items-center justify-between bg-white p-3 rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-800">Start Buying Together</p>
                <p className="text-xs text-gray-500">More members = better prices</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>

          
          <button
            onClick={() => navigate('/earn?tab=share'/* Share functionality */)}
            className="w-full flex items-center justify-between bg-white p-3 rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <Share2 className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-800">Invite Friends</p>
                <p className="text-xs text-gray-500">You both earn 50 tokens ($5)</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>
    );
  }
  
  // Active user with real data
  return (
    <div className="bg-gray-50 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-800">Your Impact</h3>
          <p className="text-sm text-gray-500">Real data from your activity</p>
        </div>
        <button
          onClick={() => navigate('/profile')}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          View All →
        </button>
      </div>
      
      {/* Token Balance Card */}
      <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl p-4 mb-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90">Token Balance</p>
            <p className="text-3xl font-bold">{formatTokens(userMetrics.tokenBalance)}</p>
            <p className="text-sm opacity-90">{formatTokenValue(userMetrics.tokenBalance)} value</p>
          </div>
          <Coins className="w-12 h-12 opacity-80" />
        </div>
        
        {/* Expiring Tokens Warning */}
        {expiringTokens.daysUntil && expiringTokens.daysUntil <= 14 && (
          <div className="mt-3 pt-3 border-t border-white/20">
            <p className="text-sm">
              ⚠️ {formatTokens(expiringTokens.amount)} tokens expire in {expiringTokens.daysUntil} days
            </p>
          </div>
        )}
      </div>
      
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <MetricCard
          label="Total Saved"
          value={`$${userMetrics.totalSaved}`}
          icon={<TrendingUp className="w-5 h-5 text-green-600" />}
          bgColor="bg-green-50"
        />
        <MetricCard
          label="Orders"
          value={userMetrics.ordersCompleted}
          icon={<ShoppingBag className="w-5 h-5 text-blue-600" />}
          bgColor="bg-blue-50"
        />
        <MetricCard
          label="Jobs Done"
          value={userMetrics.jobsCompleted}
          icon={<Award className="w-5 h-5 text-purple-600" />}
          bgColor="bg-purple-50"
        />
        <MetricCard
          label="Trust Score"
          value={userMetrics.trustScore.toFixed(1)}
          icon={<span className="text-lg">⭐</span>}
          bgColor="bg-yellow-50"
        />
      </div>
      
      {/* Token Flow */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Token Activity</h4>
        <div className="flex justify-between text-center">
          <div>
            <p className="text-lg font-bold text-green-600">+{formatTokens(userMetrics.tokensEarned)}</p>
            <p className="text-xs text-gray-500">Earned</p>
          </div>
          <div className="border-l border-gray-200" />
          <div>
            <p className="text-lg font-bold text-orange-600">-{formatTokens(userMetrics.tokensSpent)}</p>
            <p className="text-xs text-gray-500">Redeemed</p>
          </div>
          <div className="border-l border-gray-200" />
          <div>
            <p className="text-lg font-bold text-blue-600">{userMetrics.referralsMade}</p>
            <p className="text-xs text-gray-500">Referrals</p>
          </div>
        </div>
      </div>
      
      {/* CTA */}
      {userMetrics.tokenBalance > 0 && (
        <button
          onClick={() => navigate('/deals')}
          className="w-full mt-4 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Use Your Tokens →
        </button>
      )}
    </div>
  );
};

// Simple metric card component
const MetricCard = ({ label, value, icon, bgColor }) => (
  <div className={`${bgColor} rounded-lg p-3`}>
    <div className="flex items-center justify-between mb-1">
      {icon}
    </div>
    <p className="text-xl font-bold text-gray-800">{value}</p>
    <p className="text-xs text-gray-600">{label}</p>
  </div>
);

export default AnalyticsDashboard;